import React, { useEffect, useState } from "react";
import api from "../../services/api";

const formatDateTime = value => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch {
    return value;
  }
};

const minutes = value => {
  const total = Number(value || 0);

  if (total <= 0) return "-";
  if (total < 60) return `${total.toFixed(0)} min`;

  const h = Math.floor(total / 60);
  const m = Math.round(total % 60);

  if (h < 24) return `${h}h ${m}min`;

  const d = Math.floor(h / 24);
  const restH = h % 24;

  return `${d}d ${restH}h`;
};

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: 16,
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)"
};

const FollowUpDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    summary: {},
    overdueFollowUps: [],
    todayFollowUps: [],
    nextFollowUps: [],
    leadsWithoutFollowUp: [],
    sellerRanking: []
  });

  const summary = data.summary || {};
  const overdueFollowUps = data.overdueFollowUps || [];
  const todayFollowUps = data.todayFollowUps || [];
  const nextFollowUps = data.nextFollowUps || [];
  const leadsWithoutFollowUp = data.leadsWithoutFollowUp || [];
  const sellerRanking = data.sellerRanking || [];

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get("/dashboard/follow-up");
      setData(response.data || {});
    } catch (err) {
      console.error("Erro ao carregar Follow-up Inteligente:", err);
    } finally {
      setLoading(false);
    }
  };

  const openTicket = uuid => {
    if (!uuid) {
      alert("Este item não possui ticket vinculado.");
      return;
    }

    window.location.href = `/tickets/${uuid}`;
  };

  useEffect(() => {
    loadData();
  }, []);

  const renderFollowUpCard = (item, type) => (
    <div
      key={`${type}-${item.id}`}
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 12,
        marginBottom: 10,
        background: type === "overdue" ? "#fff7f7" : "#ffffff"
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ color: "#0f172a" }}>
          #{item.id} - {item.contactName || "Contato"}
        </strong>

        <span
          style={{
            color: type === "overdue" ? "#dc2626" : "#2563eb",
            fontWeight: 900
          }}
        >
          {item.status || "-"}
        </span>
      </div>

      <div style={{ color: "#475569", fontSize: 12, marginTop: 4 }}>
        Vendedor: {item.sellerName || "Sem vendedor"}
      </div>

      <div style={{ color: "#475569", fontSize: 12 }}>
        Data: {formatDateTime(item.reminderDate || item.sendAt)}
      </div>

      {type === "overdue" && (
        <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 900 }}>
          Atrasado há: {minutes(item.minutesOverdue)}
        </div>
      )}

      <div style={{ color: "#64748b", fontSize: 12, marginTop: 6 }}>
        {(item.reminderMessage || item.body || "").slice(0, 160)}
      </div>

      <button
        onClick={() => openTicket(item.ticketUuid)}
        style={{
          border: 0,
          borderRadius: 10,
          padding: "8px 12px",
          background: "#b91c1c",
          color: "#fff",
          fontWeight: 800,
          cursor: "pointer",
          marginTop: 10
        }}
      >
        Abrir atendimento
      </button>
    </div>
  );

  return (
    <div style={{ marginTop: 24, marginBottom: 32, gridColumn: "1 / -1", width: "100%" }}>
      <div
        style={{
          background: "linear-gradient(135deg, #111827 0%, #7f1d1d 55%, #dc2626 100%)",
          borderRadius: 24,
          padding: 24,
          color: "#fff",
          boxShadow: "0 20px 45px rgba(220, 38, 38, 0.22)",
          marginBottom: 18
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 13, opacity: 0.85, fontWeight: 700 }}>
              Recuperação de Vendas
            </div>

            <h2 style={{ margin: "6px 0 4px", fontSize: 28, fontWeight: 900 }}>
              Follow-up Inteligente
            </h2>

            <div style={{ opacity: 0.9 }}>
              Acompanhe follow-ups atrasados, de hoje, próximos e leads sem próxima ação.
            </div>
          </div>

          <button
            onClick={loadData}
            disabled={loading}
            style={{
              border: 0,
              borderRadius: 12,
              padding: "11px 18px",
              fontWeight: 800,
              cursor: "pointer",
              background: "#fff",
              color: "#b91c1c",
              alignSelf: "end"
            }}
          >
            {loading ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginBottom: 16 }}>
        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Follow-ups atrasados</div>
          <div style={{ color: "#dc2626", fontSize: 24, fontWeight: 900 }}>
            {summary.overdueFollowUps || 0}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Follow-ups de hoje</div>
          <div style={{ color: "#f59e0b", fontSize: 24, fontWeight: 900 }}>
            {summary.todayFollowUps || 0}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Próximos follow-ups</div>
          <div style={{ color: "#2563eb", fontSize: 24, fontWeight: 900 }}>
            {summary.futureFollowUps || 0}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Enviados/finalizados</div>
          <div style={{ color: "#16a34a", fontSize: 24, fontWeight: 900 }}>
            {summary.sentFollowUps || 0}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Leads sem próxima ação</div>
          <div style={{ color: "#ef4444", fontSize: 24, fontWeight: 900 }}>
            {summary.leadsWithoutFollowUp || 0}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div style={cardStyle}>
          <div style={{ color: "#dc2626", fontSize: 13, fontWeight: 900 }}>
            Urgente
          </div>

          <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 900, marginBottom: 12 }}>
            Follow-ups atrasados
          </div>

          {overdueFollowUps.length === 0 ? (
            <div style={{ color: "#16a34a", fontWeight: 800 }}>
              Nenhum follow-up atrasado.
            </div>
          ) : (
            overdueFollowUps.slice(0, 10).map(item => renderFollowUpCard(item, "overdue"))
          )}
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#f59e0b", fontSize: 13, fontWeight: 900 }}>
            Hoje
          </div>

          <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 900, marginBottom: 12 }}>
            Follow-ups para hoje
          </div>

          {todayFollowUps.length === 0 ? (
            <div style={{ color: "#64748b" }}>
              Nenhum follow-up para hoje.
            </div>
          ) : (
            todayFollowUps.slice(0, 10).map(item => renderFollowUpCard(item, "today"))
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={cardStyle}>
          <div style={{ color: "#2563eb", fontSize: 13, fontWeight: 900 }}>
            Próximos
          </div>

          <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 900, marginBottom: 12 }}>
            Próximos follow-ups
          </div>

          {nextFollowUps.length === 0 ? (
            <div style={{ color: "#64748b" }}>
              Nenhum próximo follow-up encontrado.
            </div>
          ) : (
            nextFollowUps.slice(0, 10).map(item => renderFollowUpCard(item, "next"))
          )}
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#ef4444", fontSize: 13, fontWeight: 900 }}>
            Oportunidades sem próxima ação
          </div>

          <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 900, marginBottom: 12 }}>
            Leads sem follow-up futuro
          </div>

          {leadsWithoutFollowUp.length === 0 ? (
            <div style={{ color: "#16a34a", fontWeight: 800 }}>
              Todos os leads abertos têm próxima ação.
            </div>
          ) : (
            leadsWithoutFollowUp.slice(0, 10).map(item => (
              <div
                key={`lead-${item.id}`}
                style={{
                  border: "1px solid #fee2e2",
                  borderRadius: 12,
                  padding: 12,
                  marginBottom: 10,
                  background: "#fff7f7"
                }}
              >
                <strong style={{ color: "#0f172a" }}>
                  #{item.id} - {item.contactName || "Contato"}
                </strong>

                <div style={{ color: "#475569", fontSize: 12, marginTop: 4 }}>
                  Vendedor: {item.sellerName || "Sem vendedor"}
                </div>

                <div style={{ color: "#475569", fontSize: 12 }}>
                  Última atualização: {formatDateTime(item.updatedAt)}
                </div>

                <button
                  onClick={() => openTicket(item.uuid)}
                  style={{
                    border: 0,
                    borderRadius: 10,
                    padding: "8px 12px",
                    background: "#b91c1c",
                    color: "#fff",
                    fontWeight: 800,
                    cursor: "pointer",
                    marginTop: 10
                  }}
                >
                  Abrir lead
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={{ color: "#64748b", fontSize: 13, fontWeight: 700 }}>
          Ranking de follow-up por vendedor
        </div>

        <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 900, marginBottom: 14 }}>
          Pendências e follow-ups enviados
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
            <thead>
              <tr style={{ color: "#64748b", textAlign: "left", fontSize: 12 }}>
                <th style={{ padding: "10px 8px" }}>#</th>
                <th style={{ padding: "10px 8px" }}>Vendedor</th>
                <th style={{ padding: "10px 8px" }}>Pendentes</th>
                <th style={{ padding: "10px 8px" }}>Atrasados</th>
                <th style={{ padding: "10px 8px" }}>Enviados</th>
              </tr>
            </thead>

            <tbody>
              {sellerRanking.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: 16, color: "#64748b" }}>
                    Nenhum dado encontrado.
                  </td>
                </tr>
              ) : (
                sellerRanking.map((seller, index) => (
                  <tr key={seller.userId} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "12px 8px", fontWeight: 900 }}>{index + 1}</td>
                    <td style={{ padding: "12px 8px", fontWeight: 800 }}>{seller.sellerName}</td>
                    <td style={{ padding: "12px 8px", color: "#2563eb", fontWeight: 900 }}>
                      {seller.pendingFollowUps || 0}
                    </td>
                    <td style={{ padding: "12px 8px", color: "#dc2626", fontWeight: 900 }}>
                      {seller.overdueFollowUps || 0}
                    </td>
                    <td style={{ padding: "12px 8px", color: "#16a34a", fontWeight: 900 }}>
                      {seller.sentFollowUps || 0}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FollowUpDashboard;
